import React from 'react';
import produce from "immer"

import { connect } from 'react-redux';

import { withStyles } from '@mui/styles';

import { EventsContext } from './EventsContext';

import Node from '../../common/Node';
import Search from '../../common/Search';
import LoaderNew from '../../common/LoaderNew';
import CommonStyles from '../../common/CommonStyles';
import EventsStyles from './EventsStyles';

import ComponentWrapper from '../../common/ComponentWrapper';
import EventsTabs from './components/EventsTabs';

import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Divider from '@mui/material/Divider';

import { setLightMetaQueryWithOps } from '../../reducer/metaReducer';

import {
  getUrlParameter,
  setUrlParameter,
  removeUrlParameter,
  getNodesIdsFromGraphTrigger,
} from '../../utils';

import {
  get,
  isEqual,
  cloneDeep,
} from '../../utils/lodash';

import PredicateChain from '../../common/PredicateChain';

import EventCalculation from './EventCalculation';

const styles = {
  ...CommonStyles,
  ...EventsStyles,
};

export const CONCEPT_SUBSCRIBER = "CONCEPT_SUBSCRIBER";
export const VALUE_EXPRESSION = "VALUE_EXPRESSION";
export const PREDICATE_SUBSCRIBER = "PREDICATE_SUBSCRIBER";
export const FIXED_TIME_SCHEDULER = "FIXED_TIME_SCHEDULER";
export const PERIOD_SCHEDULER = "PERIOD_SCHEDULER";
export const INFERENCE = "INFERENCE";
export const PREDICATE_MACROS = "PREDICATE_MACROS";
export const GRAPH_TRIGGER = "GRAPH_TRIGGER";

export const TabParams = {
  PREDICATE_SUBSCRIBER: 'sub',
  VALUE_EXPRESSION: 'val_exp',
  PREDICATE_MACROS: 'macro',
  CONCEPT_SUBSCRIBER: 'concept',
  PERIOD_SCHEDULER: 'time_period',
  FIXED_TIME_SCHEDULER: 'fixed_time_scheduler',
  INFERENCE: 'inference',
  GRAPH_TRIGGER: 'graph_trigger',
};


class EventsPage extends ComponentWrapper {
  state = {
    loaded: false,
    events: {},
    filterByConcept: null,
    filterByPids: {
      cidByFirstRst: null,
      pids: [],
    },
    currentTab: PREDICATE_MACROS,
  };

  collectNodeIdsFromEvents = events => {
    const mergedIdKeysObject = {
      ...events[CONCEPT_SUBSCRIBER],
      ...events[PREDICATE_SUBSCRIBER],
      ...events[VALUE_EXPRESSION],
      ...events[INFERENCE],
      ...events[PREDICATE_MACROS],
      ...events[GRAPH_TRIGGER],
    }

    return Object.keys(mergedIdKeysObject).map(Number);
  }

  collectNodeIdsFromInferences = events => {
    const inferenceEvents = events[INFERENCE];

    const eventsBySubscriber = Object.values(inferenceEvents).map(function(eventsBySubscriber) {
      return eventsBySubscriber;
    });

    let mergedInferences = [].concat.apply([], eventsBySubscriber);

    const ancestorIds = Object.values(mergedInferences).map(function(event) {
      return event.trigger.ancestor;
    });

    return ancestorIds;
  }

  collectTrplsNodeIdsFromGraphTriggers = (events, setup_rbox_light) => {
    const cids = [];

    if (events.INFERENCE && Object.keys(events.INFERENCE).length) {
      for (let sub of Object.keys(events.INFERENCE)) {
        cids.push(...getNodesIdsFromGraphTrigger(events.INFERENCE[sub], setup_rbox_light));
      }
    }

    if (events.GRAPH_TRIGGER && events.GRAPH_TRIGGER.length)
      cids.push(...getNodesIdsFromGraphTrigger(events.GRAPH_TRIGGER, setup_rbox_light));

    return cids;
  };

  collectMacroArgNodeIdsFromPredicateMacros = (events) => {
    const cids = [];

    if (events.PREDICATE_MACROS) {
      for (const cid of Object.keys(events.PREDICATE_MACROS)) {
        const predMacros = events.PREDICATE_MACROS[cid];
        for (const predMacro of predMacros) {
          if (!predMacro.macro || !predMacro.macro.args) continue;
          for (const arg of predMacro.macro.args) {
            if (arg.concept) cids.push(arg.concept);
          }
        }
      }
    }

    return cids;
  };

  componentDidMount() {
    document.title="Events | Milkyweb";

    this.getEvents();
  };

  getEvents() {
    const { setup, setLightMetaQueryWithOps } = this.props;
    const { filterByPids } = this.state;

    const cid = getUrlParameter('cid');
    const pids = getUrlParameter('pids');

    const currentTab = this.getEventTypeByTabParam();
    if (cid || pids) {
      removeUrlParameter('tab');
    } else {
      setUrlParameter('tab', TabParams[currentTab]);
    }

    this.get({
      url: `/meta_query?meta_query=["events","setup_rbox_light","predicates_ops"]`,
      cb: ({ _meta_query }) => {
        const events = _meta_query.events || {};
        const predicates_ops = _meta_query.predicates_ops || {};
        const setup_rbox_light = _meta_query.setup_rbox_light || {};

        const light_meta_and_ops = { predicates_ops: predicates_ops, setup_rbox_light: setup_rbox_light };

        setLightMetaQueryWithOps({ light_meta_and_ops });

        // convert node ids from string to integer type
        const nodeIds = this.collectNodeIdsFromEvents(events);
        const inferenceEventsIds = this.collectNodeIdsFromInferences(events);
        const trplsInferenceNodeIds = this.collectTrplsNodeIdsFromGraphTriggers(events, setup_rbox_light);
        const macroArgNodeIds = this.collectMacroArgNodeIdsFromPredicateMacros(events);

        const collectedIds = nodeIds.concat(inferenceEventsIds, trplsInferenceNodeIds, macroArgNodeIds);
        if (cid && !collectedIds.includes(cid)) collectedIds.push(cid);

        if (events.GRAPH_TRIGGER) events.GRAPH_TRIGGER = events.GRAPH_TRIGGER.reverse();
        if (events.PERIOD_SCHEDULER) events.PERIOD_SCHEDULER = events.PERIOD_SCHEDULER.reverse();
        if (events.FIXED_TIME_SCHEDULER) events.FIXED_TIME_SCHEDULER = events.FIXED_TIME_SCHEDULER.reverse();

        const data = {
          api_key: setup.id,
          filter: {
            nodes: collectedIds,
            page: 1,
            limit: 300,
          },
        };

        const newFilterByPids = { ...filterByPids };
        if (pids) {
          const pidsArray = JSON.parse(pids);
          if (Array.isArray(pidsArray) && pidsArray.length) {
            newFilterByPids.pids = pidsArray;
            if (cid) {
              newFilterByPids.cidByFirstRst = cid;
            } else {
              const matchedCid = this.getFirstMatchedRstConceptId(pidsArray, setup_rbox_light);
              if (matchedCid) newFilterByPids.cidByFirstRst = matchedCid;
            }
          }
        }

        // get node data for displaying concepts
        this.postWithData({
          url: `/pnkqry3`,
          data,
          cb: ({ nodes }) => {
            const data = {
              events,
              currentTab,
              loaded: true,
              nodesData: nodes,
              filterByPids: newFilterByPids,
              filterByConcept: cid && nodes[cid],
            };

            this.setState(data, () => {
              this.filterEvents();
            });
          },
        });
      },
    });
  }

  getEventTypeByTabParam = () => {
    const tabParam = getUrlParameter('tab');
    return tabParam
      ? Object.keys(TabParams).find(key => TabParams[key] === tabParam)
      : PREDICATE_MACROS;
  };

  getFirstMatchedRstConceptId = (pids, setup_rbox_light) => {
    if (!setup_rbox_light || !pids || !pids.length) return;

    for (const cid of Object.keys(setup_rbox_light)) {
      const rbox = get(setup_rbox_light, [cid, 'box', 'r']);
      if (!rbox) continue;
      for (const cid2 of Object.keys(rbox)) {
        for (const rst of rbox[cid2]) {
          if (rst.id === pids[0]) return rst.sid;
        }
      }
    }
  };

  filterEvents = () => {
    const {
      events,
      filterByPids,
      filterByConcept,
    } = this.state;

    if (!events) return events;

    const filterCid = filterByConcept && filterByConcept.id;
    const filterPids = filterByPids.pids;

    if (!filterCid && !filterPids.length) {
      if (this.state.filteredEventIds)
        this.setState({ filteredEventIds: null });
      return events;
    }

    /* PERIOD_SCHEDULER type ignore. It has not filter */
    const filteredEventIds = {
      PREDICATE_SUBSCRIBER: [],
      VALUE_EXPRESSION: [],
      PREDICATE_MACROS: [],
      CONCEPT_SUBSCRIBER: [],
      INFERENCE: [],
      GRAPH_TRIGGER: [],
    };

    for (const type of [PREDICATE_SUBSCRIBER, VALUE_EXPRESSION, PREDICATE_MACROS, CONCEPT_SUBSCRIBER, INFERENCE]) {
      if (!events[type]) continue;

      for (const cid of Object.keys(events[type])) {
        if (type !== INFERENCE && filterCid && +filterCid !== +cid) continue;

        for (const event of events[type][cid]) {
          if (event.editable) continue;

          if (type === INFERENCE) {
            if (filterCid && (+filterCid !== +cid && +filterCid !== +get(event, ['trigger', 'ancestor']))) {
              continue;
            }
          }

          let isMatched;

          if (filterPids.length) {
            if (type === PREDICATE_SUBSCRIBER && EventCalculation.isMatchedPredicateSubOrValueExp(event, filterPids)) {
              isMatched = true;
            } else if (type === VALUE_EXPRESSION && EventCalculation.isMatchedPredicateSubOrValueExp(event, filterPids)) {
              isMatched = true;
            } else if (type === PREDICATE_MACROS && EventCalculation.isMatchedPredicateMacros(event, filterPids)) {
              isMatched = true;
            } else if (type === INFERENCE && EventCalculation.isMatchedGraphTrigger(event, filterPids)) {
              isMatched = true;
            }
          } else {
            isMatched = true;
          }

          if (isMatched) filteredEventIds[type].push(event.id);
        }
      }
    }

    if (events[GRAPH_TRIGGER]) {
      for (const event of events[GRAPH_TRIGGER]) {
        if (filterCid && +filterCid !== +get(event, ['trigger', 'ancestor'])) continue;
        if (filterPids.length && !EventCalculation.isMatchedGraphTrigger(event, filterPids)) continue;
        filteredEventIds.GRAPH_TRIGGER.push(event.id);
      }
    }

    this.setState({ filteredEventIds });
  };

  calculateEventsNumber = (eventTab, type) => {
    let calculatedEventsNumber = 0;
    if (eventTab && type !== 'Time period' && type !== 'Fixed time scheduler' && type !== 'Graph trigger'){
      Object.values(eventTab).forEach(ev => {
        if (ev !== null)
        calculatedEventsNumber += ev.length;
      })
    } else if (eventTab) {
      calculatedEventsNumber += Object.values(eventTab).length;
    }
    return calculatedEventsNumber;
  };

  onFilterConceptClick = (filterByConcept) => {
    this.setState({
      filterByConcept,
    }, () => {
      this.filterEvents();
    });
  };

  onRemoveFilterByConceptClick = () => {
    this.setState({
      filterByConcept: null,
    }, () => {
      this.filterEvents();
    });
  };

  // fuck, we need a single source of data to avoid this shit
  addNodeToNodesData = (node) => {
    if (node && node.id)
    this.setState({ nodesData: { ...this.state.nodesData, [node.id]: node } })
  }

  openRstToEditEvent = (ancestor, rstId) => {
    window.open(`/rst_edit?id=${ancestor}&pid=${rstId}`, '_blank', 'noreferrer noopener').focus();
  };

  createEvent = (subscriberEvent, type) => {
    const nextImmutableEvents = produce(this.state.events, draftEvents => {
      const subData = {
        type,
        editable: true,
        draft_id: 'draft' + Date.now(),
      };

      if (type === PREDICATE_SUBSCRIBER) {
        subData.function = '';
        subData.pidsChain = [];
        subData.triggers = [];
        subData.subscriber = [];
      } else if (type === CONCEPT_SUBSCRIBER) {
        subData.function = '';
        subData.triggers = [];
      } else if (type === PERIOD_SCHEDULER) {
        subData.function = '';
        subData.triggers = [];
        subData.title = 'New Time Period Event';
        subData.enabled = false;
      } else if (type === FIXED_TIME_SCHEDULER) {
        subData.function = '';
        subData.triggers = [];
        subData.title = 'New Fixed Time Event';
        subData.enabled = false;
      } else if (type === INFERENCE) {
        subData.trigger = {
          ancestor: null,
          trpls: [],
        }
      }

      if (type === GRAPH_TRIGGER) {
        subData.trigger = {
          ancestor: null,
          trpls: [],
        }
        subData.macro = {
          script_type: '',
          return_type: '',
          code: '',
        }
      }

      if (subscriberEvent) {

        if (draftEvents[type]) {
          draftEvents[type].draft = subData;

        }
        else {
          draftEvents[type] = { draft: subData };
        }

      } else if (draftEvents[type]) {
        draftEvents[type].unshift(subData)

      } else {
        draftEvents[type] = [subData];
      }
    });

    this.setState({ events: nextImmutableEvents });
  };

  addSubscriber = (subscriber, type, id) => {
    const nextImmutableEvents = produce(this.state.events, draftEvents => {
      const cloneDraftedEvent = cloneDeep(draftEvents[type].draft);
      if (type !== 'PREDICATE_SUBSCRIBER') cloneDraftedEvent.subscriber = subscriber;

      if (draftEvents[type] && draftEvents[type][subscriber]) {
        draftEvents[type][subscriber].unshift(cloneDraftedEvent);
      } else if (!draftEvents[type]) {
        draftEvents[type] = { [subscriber]: [cloneDraftedEvent] };
      } else {
        draftEvents[type] = { [subscriber]: [cloneDraftedEvent], ...draftEvents[type] };
      }

      delete draftEvents[type].draft;
    });
    
    this.setState({ events: nextImmutableEvents }, () => {
      setTimeout(() => {
        const el = document.getElementById(id);
        el.scrollIntoView({ block: "center", behavior: 'smooth' });
      }, 100)
    });
  };

  findEventIndex = (event, eventId, isDraft) => {
    if (isDraft) {
      if (event.draft_id === String(eventId)) return true;
    } else {
      if (+event.id === +eventId) return true;
    }
  };

  saveEvent = (event, cb) => {
    if (event.hasOwnProperty('editable')) delete event.editable;

    this.postWithData({
      url: '/rest/upd_event',
      data: {
        ...event,
      },
      cb: (data) => {
        if (cb) cb(data.event);
      },
    });
  };

  updateEvent = (nodeId, type, event, cb) => {
    let draftId;
    if (event.hasOwnProperty('draft_id')) {
      draftId = event.draft_id;
      delete event.draft_id;
    }

    this.saveEvent(event, (updatedEvent) => {
      if (updatedEvent) {
        const updEventId = draftId || updatedEvent.id;

        const nextImmutableEvents = produce(this.state.events, draftEvents => {
          if (nodeId) {
            const eventIndex = draftEvents[type][nodeId].findIndex(e => this.findEventIndex(e, updEventId, !!draftId));
            if (eventIndex > -1) draftEvents[type][nodeId][eventIndex] = updatedEvent;
          } else {
            const eventIndex = draftEvents[type].findIndex(e => this.findEventIndex(e, updEventId, !!draftId))
            if (eventIndex > -1) draftEvents[type][eventIndex] = updatedEvent;
          }
        });

        this.setState({
          events: nextImmutableEvents
        });

        if (cb) cb(updatedEvent);
      }
    });
  };

  removeDraftEvent = (type) => {
    const nextImmutableEvents = produce(this.state.events, draftEvents => {
      delete draftEvents[type].draft;
    });

    this.setState({ events: nextImmutableEvents });
  };

  deleteEvent = (nodeId, type, eventId) => {
    if (!eventId) return;

    const isDraft = String(eventId).startsWith('draft');

    const nextImmutableEvents = produce(this.state.events, draftEvents => {
      if (nodeId) {
        const eventIndex = draftEvents[type][nodeId].findIndex(e => this.findEventIndex(e, eventId, isDraft));
        if (eventIndex > -1) draftEvents[type][nodeId].splice(eventIndex, 1);
      } else {
        const eventIndex = draftEvents[type].findIndex(e => this.findEventIndex(e, eventId, isDraft));
        if (eventIndex > -1)  draftEvents[type].splice(eventIndex, 1);
      }
    });

    this.setState({ events: nextImmutableEvents });

    if (!isDraft) {
      this.postWithData({
        url: `/rest/del_event`,
        data: {
          id: eventId,
        }
      });
    }
  };

  collectConceptsForFilterByPredicate = () => {
    const { events, filterByConcept } = this.state;

    if (filterByConcept) return [filterByConcept.id];
    const concepts = [];

    if (events && !isEqual(events, {})) {
      const type = this.getEventTypeByTabParam();
      if (!events[type]) return concepts;

      switch (type) {
        case PREDICATE_SUBSCRIBER:
        case VALUE_EXPRESSION:
        case PREDICATE_MACROS:
          concepts.push(...Object.keys(events[type]).map(Number));
          break;
        case INFERENCE:
          for (const cid of Object.keys(events[type])) {
            Array.isArray(events[type][cid]) && events[type][cid].forEach(inf => {
              const anc = get(inf, '[trigger][ancestor]');
              if (anc && !concepts.includes(anc)) concepts.push(+anc);
            });
          }
          break;
        case GRAPH_TRIGGER:
          if (events[type]) {
            for (const trigger of events[type]) {
              const anc = get(trigger, '[trigger][ancestor]');
              if (anc && !concepts.includes(anc)) concepts.push(+anc);
            }
          }
          break;
        default:
          break;
      }
    }

    return concepts;
  };

  render() {
    const {
      setup,
      classes,
    } = this.props;

    const {
      events,
      loaded,
      nodesData,
      currentTab,
      filterByPids,
      filterByConcept,
      filteredEventIds,
    } = this.state;

    const ancestorForPredicateChainFilter = filterByPids.cidByFirstRst || this.collectConceptsForFilterByPredicate();

    return (
      <div>
        <div className={classes.tabsContainer}>

          <h3 className={classes.pageHeading}>Events</h3>

          <Tabs
            className={classes.tabs}
            value={filteredEventIds ? null : currentTab}
            onChange={(event, newTab) => {
              setUrlParameter('tab', TabParams[newTab]);

              const cid = getUrlParameter('cid');
              if (cid) removeUrlParameter('cid');
              const pids = getUrlParameter('pids');
              if (pids) removeUrlParameter('pids');

              this.setState({
                currentTab: newTab,
                filterByConcept: null,
                filterByPids: {
                  cidByFirstRst: null,
                  pids: [],
                },
              }, () => {
                this.filterEvents();
              });
            }}
          >
            <Tab value={PREDICATE_MACROS} label={`Predicate macros (${this.calculateEventsNumber(events.PREDICATE_MACROS, 'Predicate macros')})`}/>
            <Tab value={PREDICATE_SUBSCRIBER} label={`Predicate (${this.calculateEventsNumber(events.PREDICATE_SUBSCRIBER, 'Predicate')})`}/>
            <Tab value={VALUE_EXPRESSION} label={`Value expression (${this.calculateEventsNumber(events.VALUE_EXPRESSION, 'Value expression')})`}/>
            <Tab value={GRAPH_TRIGGER} label={`Graph trigger (${this.calculateEventsNumber(events.GRAPH_TRIGGER, 'Graph trigger')})`}/>
            <Tab value={INFERENCE} label={`Inferences (${this.calculateEventsNumber(events.INFERENCE, 'Inferences')})`}/>
            <Tab value={CONCEPT_SUBSCRIBER} label={`Concept (${this.calculateEventsNumber(events.CONCEPT_SUBSCRIBER, 'Concept')})`}/>
            <Tab value={PERIOD_SCHEDULER} label={`Time period (${this.calculateEventsNumber(events.PERIOD_SCHEDULER, 'Time period')})`}/>
            <Tab value={FIXED_TIME_SCHEDULER} label={`Fixed time scheduler (${this.calculateEventsNumber(events.FIXED_TIME_SCHEDULER, 'Fixed time scheduler')})`}/>
          </Tabs>
        </div>
        <Divider light />

        {currentTab !== PERIOD_SCHEDULER &&
          <div
            style={{ display: 'flex', alignItems: 'end', paddingBottom: '15px', marginBottom: '10px' }}
            className={`${classes.filterContainer}`}
          >
            <div style={{ alignSelf: 'center', marginRight: '25px', width: '222px' }}>
              <span className={`${classes.columnHeadingGeneral} ${classes.mb10}`}>Filter by concept</span>
              <div className={`${classes.mt5}`}>
                {filterByConcept ? (
                  <Node
                    isLink
                    size="sm"
                    isRemovable
                    node={filterByConcept}
                    onRemoveClick={() => {
                      removeUrlParameter('cid');
                      this.onRemoveFilterByConceptClick();
                    }}
                  />
                ) : (
                  <Search
                    conceptsOnly={true}
                    containerStyles={{
                      height: 24,
                    }}
                    onDropdownElementClick={({ node }) => {
                      removeUrlParameter('tab');
                      setUrlParameter('cid', node.id);
                      this.onFilterConceptClick(node);
                    }}
                  />
                )}
              </div>
            </div>

            {(currentTab !== CONCEPT_SUBSCRIBER) && (ancestorForPredicateChainFilter.length ||
              Number.isInteger(ancestorForPredicateChainFilter)) ? (
              <div>
                <span className={`${classes.columnHeadingGeneral} ${classes.mb10}`}>Filter by predicate</span>
                <div className={`${classes.mt5}`} style={{ minWidth: '222px' }}>
                  <PredicateChain
                    multiAncestor
                    rst={filterByPids.pids.length ? filterByPids.pids : null}
                    mode={'edit'}
                    ancestor={ancestorForPredicateChainFilter}
                    addInnerRstCb={({ newSelected, cidByFirstRst }) => {
                      removeUrlParameter('tab');
                      setUrlParameter('pids', `[${newSelected}]`);

                      const newFilterByPids = { ...filterByPids, pids: [...newSelected] };
                      if (newSelected.length === 1) newFilterByPids.cidByFirstRst = cidByFirstRst;

                      this.setState({
                        filterByPids: newFilterByPids,
                      }, () => {
                        this.filterEvents();
                      });
                    }}
                    onRemoveRstCb={(props, newSelected) => {
                      const newFilterByPids = { ...filterByPids, pids: [...newSelected] };

                      if (!newSelected.length) {
                        newFilterByPids.cidByFirstRst = null;
                        removeUrlParameter('pids');
                      } else {
                        setUrlParameter('pids', `[${newSelected}]`);
                      }

                      this.setState({
                        filterByPids: newFilterByPids,
                      }, () => {
                        this.filterEvents();
                      });
                    }}
                  />
                </div>
              </div>
            ) : null}
          </div>
        }

        {!loaded ? (
          <div className={classes.loaderContainer}>
            <LoaderNew />
          </div>
        ) : (
          <EventsContext.Provider
            value={{
              setup,
              nodesData,
              updateEvent: this.updateEvent,
              deleteEvent: this.deleteEvent,
              createEvent: this.createEvent,
              addSubscriber: this.addSubscriber,
              macro_types: this.props.macro_types,
              removeDraftEvent: this.removeDraftEvent,
              addNodeToNodesData: this.addNodeToNodesData,
              openRstToEditEvent: this.openRstToEditEvent,
            }}
          >
            <div className={classes.mainContainer}>
              <EventsTabs
                events={events}
                filteredEventIds={filteredEventIds}
                filterByConcept={filterByConcept}
                filterByPids={filterByPids}
                currentTab={currentTab}
              />
            </div>
          </EventsContext.Provider>
        )}
      </div>
    );
  }
}

export default connect(
  (state) => ({
    setup: state.meta.setup,
    macro_types: state.meta.macro_types,
  }), { setLightMetaQueryWithOps }
)(withStyles(styles)(EventsPage));
